import React, { useCallback, useEffect, useState } from 'react';
import { Button, Divider, Modal, Typography } from 'antd';
import { t } from 'helpers/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { showError, showLoading, showSuccess } from 'store/popup/actions';
import SelectorItem from 'components/SelectorItem';
import { ServiceVersion } from 'data/terra/ServiceVersion';
import Terra from 'services/apis/terra/Terra';
import { UndefinedAble } from 'data/base';
import { Service } from 'data/terra/Service';
import { firstOrUndefined } from 'util/array';
import BaseAddEntityPopup, {
  EntityDesc,
} from '../BaseAddEntityPopup/BaseAddEntityPopup';
import './ServiceDetailPopup.scss';
import {
  getBlankServiceConfig as getBlankServiceConfigData,
  ServiceName,
} from 'data/terra/ServiceImpl';
import {
  BaseConfig,
  ServiceConfig,
  ServiceConfigDescription,
} from 'data/terra/ServiceConfig';
import { RootState } from 'store/reducer';
import { Environment } from 'data/terra/Environment';
import ServiceJsonConfigBoard from 'containers/Product/Applications/components/ServiceJsonConfigBoard/ServiceJsonConfigBoard';

export interface ServiceConfigPopupProps {
  service: Service;
  handleCloseModal: () => void;
}

function ServiceConfigPopup(props: ServiceConfigPopupProps) {
  const dispatch = useDispatch();
  const [versions, setVersions] = useState<ServiceVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<
    UndefinedAble<ServiceVersion>
  >();
  const [isAddingVersion, setIsAddingVersion] = useState<boolean>(false);
  const [configs, setConfigs] = useState<ServiceConfig<BaseConfig>[]>([]);
  const { environments } = useSelector((state: RootState) => state.common);
  const [selectedEnv, setSelectedEnv] = useState<UndefinedAble<Environment>>();
  const [edittingConfig, setEdittingConfig] = useState<
    ServiceConfigDescription<BaseConfig>
  >(getBlankServiceConfigData(props.service.code as ServiceName));
  const [isConfigValid, setIsConfigValid] = useState<boolean>(true);
  const [existingConfigId, setExistingConfigId] = useState<
    UndefinedAble<string>
  >();

  const fetchServiceVersions = useCallback(async () => {
    dispatch(showLoading());
    const result = await Terra.service.fetchServiceVersions(props.service.id);
    if (result.isSuccess()) {
      const versions = result.get()!.data;
      const selectedVersion = firstOrUndefined(versions);
      setVersions(versions);
      setSelectedVersion(selectedVersion);

      if (selectedVersion) {
        const versionResult = await Terra.service.fetchServiceVersionConfigs(
          props.service.id,
          selectedVersion.id
        );
        if (versionResult.isSuccess()) {
          dispatch(showSuccess());
          const configs = versionResult.get()!.data;
          setConfigs(configs);
        } else {
          dispatch(showError(versionResult.exception().message));
        }
      } else {
        dispatch(showSuccess());
      }
    } else {
      dispatch(showError(result.exception().message));
    }
  }, [dispatch, props.service]);

  useEffect(() => {
    fetchServiceVersions();
  }, [fetchServiceVersions]);

  const onSaveServiceVersion = async (data: EntityDesc) => {
    return Terra.service.addServiceVersion(props.service.id, {
      service: { id: props.service.id },
      code: data.code,
      status: '',
      note: '',
    });
  };

  const onSaveServiceVersionConfig = async () => {
    if (!isConfigValid) {
      dispatch(showError(t('Invalid configuration')));
      return;
    }

    if (selectedVersion && selectedEnv) {
      let result;
      dispatch(showLoading());
      if (!existingConfigId) {
        result = await Terra.service.addServiceVersionConfig(
          props.service.id,
          selectedVersion.id,
          {
            env: { id: selectedEnv.id },
            serviceVersion: { id: selectedVersion.id },
            ...edittingConfig,
          }
        );
      } else {
        result = await Terra.service.updateServiceVersionConfig(
          props.service.id,
          selectedVersion.id,
          existingConfigId,
          edittingConfig
        );
      }

      if (result.isSuccess()) {
        dispatch(showSuccess());
        props.handleCloseModal();
      } else {
        dispatch(showError(result.exception().message));
      }
    }
  };

  return (
    <Modal
      visible={true}
      title={props.service.name}
      onCancel={props.handleCloseModal}
      onOk={onSaveServiceVersionConfig}
      width={768}
    >
      <div className="service-detail-row">
        <SelectorItem
          label={t('Version')}
          helperText={t('Choose Version')}
          value={selectedVersion?.code}
          onChange={(value: string) => {
            setSelectedVersion(versions.find(p => p.id === value));
          }}
          options={versions.map((v: ServiceVersion) => ({
            key: v.id.toString(),
            value: v.code,
          }))}
          marginBottom={true}
          uiMode="horizontal"
        />
        <Button
          type="primary"
          onClick={() => {
            setIsAddingVersion(true);
          }}
          style={{ marginLeft: '8px' }}
        >
          {t('Add Version')}
        </Button>
      </div>
      <SelectorItem
        label={t('Environment')}
        helperText={t('Choose Environment')}
        value={selectedEnv?.code}
        onChange={(value: string) => {
          setSelectedEnv(environments.find(p => p.id === value));
          const config = configs.find(c => c.env.id === value);
          if (config) {
            setExistingConfigId(config.id);
            setEdittingConfig(config);
          } else {
            setExistingConfigId(undefined);
            setEdittingConfig(
              getBlankServiceConfigData(props.service.code as ServiceName)
            );
          }
        }}
        options={environments.map((v: Environment) => ({
          key: v.id.toString(),
          value: v.code,
        }))}
        marginBottom={true}
        uiMode="horizontal"
      />
      <Divider />
      {!selectedVersion && (
        <Typography.Text style={{ marginBottom: '8px' }} strong>
          {t('Select version or create one before config')}
        </Typography.Text>
      )}
      {selectedVersion && (
        <Typography.Text style={{ marginBottom: '8px' }} strong>
          {t('Service Version Configuration Notice')}
        </Typography.Text>
      )}
      {selectedVersion && selectedEnv && (
        <ServiceJsonConfigBoard
          showEnabled={false}
          serviceName={props.service.code as ServiceName}
          serviceConfig={edittingConfig}
          onToggleService={() => {}}
          onEditServiceConfig={(
            data: ServiceConfigDescription<any>,
            isValid: boolean
          ) => {
            setEdittingConfig(data);
            setIsConfigValid(isValid);
          }}
        />
      )}
      {isAddingVersion && (
        <BaseAddEntityPopup<ServiceVersion>
          popupTitle={t(`Add Version`)}
          onSaveEntity={onSaveServiceVersion}
          onSaveEntityResult={() => fetchServiceVersions()}
          handleCloseModal={() => {
            setIsAddingVersion(false);
          }}
        />
      )}
    </Modal>
  );
}

export default ServiceConfigPopup;
